import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, TextField, Checkbox } from '@mui/material';

// ============================================
// Error Animation
// ============================================
export const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

export const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  display: 'inline-flex',
  borderRadius: '4px',
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

export const ErrorCheckboxWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  display: 'inline-flex',
  borderRadius: '4px',
  padding: '4px',
  ...(hasError && {
    border: '2px solid #dc2626',
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

// ============================================
// Modal Container
// ============================================
export const ModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
});

export const ModalContainer = styled(Box)({
  width: '580px',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
});

// ============================================
// Header
// ============================================
export const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: '#262626',
});

export const ModalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
});

export const CloseButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#fff',
  '&:hover': {
    opacity: 0.8,
  },
});

// ============================================
// Content
// ============================================
export const ModalContent = styled(Box)({
  padding: '24px',
  maxHeight: 'calc(90vh - 140px)',
  overflowY: 'auto',
});

export const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '20px',
});

export const FormLabel = styled(Typography)({
  width: '80px',
  minWidth: '80px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
});

export const FormValue = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// ============================================
// Date Picker
// ============================================
export const DatePickerWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  border: '1px solid #d9d9d9',
  borderRadius: '6px',
  backgroundColor: '#fff',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#0F7F85',
  },
});

export const DateText = styled(Typography)({
  fontSize: '14px',
  color: '#262626',
});

// ============================================
// Input
// ============================================
export const StyledInput = styled(TextField)({
  width: '120px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    '& input': {
      padding: '8px 12px',
      fontSize: '14px',
    },
  },
});

export const InputSuffix = styled(Typography)({
  fontSize: '14px',
  color: '#262626',
});

// ============================================
// Checkbox
// ============================================
export const CheckboxGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const CheckboxItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const StyledCheckbox = styled(Checkbox)({
  padding: '4px',
  '&.Mui-checked': {
    color: '#0F7F85',
  },
});

export const CheckboxLabel = styled(Typography)({
  fontSize: '14px',
  color: '#262626',
});

// ============================================
// Count Text
// ============================================
export const CountText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#0F7F85',
});

// ============================================
// Memo
// ============================================
export const MemoTextarea = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& textarea': {
      fontSize: '14px',
    },
  },
});

// ============================================
// Info Section
// ============================================
export const InfoSection = styled(Box)({
  marginTop: '20px',
  padding: '16px',
  backgroundColor: '#f9f9f9',
  borderRadius: '8px',
});

export const InfoList = styled('ul')({
  margin: 0,
  paddingLeft: '20px',
});

export const InfoItem = styled('li')({
  fontSize: '13px',
  color: '#666',
  lineHeight: 1.8,
  '& .highlight': {
    color: '#EE735A',
    cursor: 'pointer',
  },
});

export const InfoHighText = styled('span')({
  fontSize: '13px',
  color: '#EE735A',
  lineHeight: 1.8,
  '& .highlight': {
    color: '#EE735A',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});

// ============================================
// Footer
// ============================================
export const ModalFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '20px 24px',
  borderTop: '1px solid #e5e5e5',
});

export const CancelButton = styled(Button)({
  minWidth: '100px',
  height: '44px',
  padding: '0 24px',
  borderRadius: '8px',
  backgroundColor: '#e5e5e5',
  color: '#262626',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#d5d5d5',
  },
});

export const SubmitButton = styled(Button)({
  minWidth: '160px',
  height: '44px',
  padding: '0 24px',
  borderRadius: '8px',
  backgroundColor: '#0F7F85',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#0a6a6f',
  },
  '&:disabled': {
    backgroundColor: '#ccc',
    color: '#999',
  },
});
